/**
 * Modern Product Card Data Manager
 * Uses GraphQL Storefront API to populate product cards with location-specific data
 */

// Debug mode - set to false for production
const PRODUCT_CARD_DEBUG = false;

class ProductCardDataManager {
  constructor() {
    this.DEBUG = PRODUCT_CARD_DEBUG;
    this.locationManager = window.locationManager;
    this.batchQueue = [];
    this.batchTimeout = null;
    this.batchDelay = 100; // 100ms debounce
    this.isProcessing = false;
    this._addLabelMarkup = null; // cached "add to cart" label markup
  }

  /**
   * Markup for the quick-add "add to cart" label. Captured once from an in-stock card on the
   * page so it matches the theme/locale exactly; falls back to a constant if none is present.
   */
  getAddLabelMarkup() {
    if (this._addLabelMarkup) return this._addLabelMarkup;
    const label = document.querySelector('.quick-add__submit > span[data-add-to-cart-label]');
    const savedLabel = label && label.getAttribute('data-add-to-cart-label');
    if (savedLabel && savedLabel.trim()) {
      this._addLabelMarkup = savedLabel.trim();
      return this._addLabelMarkup;
    }
    this._addLabelMarkup = 'Add to cart';
    return this._addLabelMarkup;
  }

  /**
   * Relabel a quick-add button to its "add to cart" state.
   * Availability here is location-based: when a card is confirmed in stock at the picking
   * store, the server-rendered "Sold out" label (driven by Shopify's global variant.available,
   * which ignores the picking store) must be corrected to the add state. Cards NOT available at
   * the location are removed/hidden elsewhere, so this only runs on genuinely-addable cards.
   */
  restoreAddButtonLabel(container) {
    try {
      const submit = container.querySelector('.quick-add__submit, .product-form__submit');
      if (!submit) return;
      const label = submit.querySelector(':scope > span:not(.sold-out-message)');
      if (!label) return;
      const addLabel = label.getAttribute('data-add-to-cart-label') || this.getAddLabelMarkup();
      if (label.textContent.trim() === addLabel) return;
      label.textContent = addLabel;
      label.setAttribute('data-add-to-cart-label', addLabel);
    } catch (e) {
      if (this.DEBUG) console.warn('restoreAddButtonLabel failed:', e);
    }
  }

  /**
   * True only when the shopper has EXPLICITLY chosen a picking store - see
   * location-manager.js hasExplicitPickingStore(). Option B pre-store browse
   * (PICKING-STORE-AND-SLOT-SPLIT-PLAN.md "Still deciding") keys every
   * display/filter decision off this, never off getCurrentLocation() (which
   * silently defaults to store 76 for anonymous visitors).
   */
  hasExplicitStore() {
    return typeof window.hasPickingStore === 'function' && window.hasPickingStore();
  }

  preserveDigitalProduct(container) {
    const card = container.closest('.product-item') || container;
    const marker = '[data-gift-card-product], [data-giftcard="yes"], [data-deliverypass="yes"]';
    if (!card.matches(marker) && !card.querySelector(marker)) return false;

    // Gift cards and Delivery Pass use the price and variant rendered by Liquid.
    // Their ERP ranging is not store inventory. Leave purchase/login/fulfilment
    // guards and form values intact, including requires-shipping metadata.
    card.classList.remove('location-unavailable');
    card.querySelectorAll('.dynamic-price__wrapper').forEach((wrapper) => {
      wrapper.classList.remove('price-visually-hidden');
      wrapper.classList.add('price-loaded');
    });
    return true;
  }

  isEachUom(uom) {
    const raw = (uom || '').toString().trim().toLowerCase();
    const normalized = raw
      .replace(/\$/g, '')
      .replace(/\d+(?:\.\d+)?/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return normalized === 'each' ||
      normalized === 'ea' ||
      normalized.includes('/ each') ||
      normalized.includes('per each') ||
      /(^|[\s/])each\b/.test(normalized) ||
      /(^|\s)ea\b/.test(normalized);
  }

  /**
   * custom.special_locations is the only source of truth for special status.
   * Accepts a JSON string, array, or metafield-like { value }.
   */
  isOnSpecialAtLocation(specialLocationsRaw, location) {
    if (specialLocationsRaw == null || specialLocationsRaw === '' || location == null) {
      return false;
    }

    let specialLocations = specialLocationsRaw;
    if (typeof specialLocations === 'object' && specialLocations !== null && 'value' in specialLocations) {
      specialLocations = specialLocations.value;
    }

    if (typeof specialLocations === 'string') {
      try {
        specialLocations = JSON.parse(specialLocations);
      } catch (e) {
        return false;
      }
    }

    if (!Array.isArray(specialLocations)) return false;
    const loc = String(location);
    return specialLocations.some((entry) => String(entry) === loc);
  }

  // Cup / comparison UOM under the price (e.g. "$1.77 / litre") — keep ERP value.
  formatCupUom(uom) {
    const raw = (uom || '').toString().trim();
    if (!raw) return raw;
    return raw
      .replace(/^\/\s*/, '/ ')
      .replace(/^per\s+/i, '/ ')
      .replace(/\beach\b/gi, 'ea');
  }

  // Selling unit beside the main price — always "ea" (never litre/kg from the UOM metafield).
  setSellingUnitLabel(fromPriceElement, label = 'ea') {
    if (!fromPriceElement) return;
    const parentStrong = fromPriceElement.parentElement;
    if (!parentStrong || parentStrong.tagName !== 'STRONG') return;
    let uomElement = parentStrong.querySelector('small');
    if (!uomElement) {
      parentStrong.appendChild(document.createTextNode(' '));
      uomElement = document.createElement('small');
      parentStrong.appendChild(uomElement);
    }
    uomElement.textContent = label;
  }

  isProduceCard(productCard, productData = null) {
    const productType = [
      productCard?.getAttribute('data-product-type'),
      productData?.productType
    ].filter(Boolean).join(' ').toLowerCase();
    const productTags = [
      productCard?.getAttribute('data-product-tags'),
      Array.isArray(productData?.tags) ? productData.tags.join(' ') : productData?.tags
    ].filter(Boolean).join(' ').toLowerCase();
    const hasProducePriceClass = Boolean(productCard?.querySelector('.unit_price.price-special-color'));

    return hasProducePriceClass ||
      productType.includes('fruit') ||
      productType.includes('veg') ||
      productTags.includes('fruit') ||
      productTags.includes('vegetable');
  }

  hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement, productData = null) {
    if (!targetElement) return false;

    if (this.isEachUom(uom) && this.isProduceCard(productCard, productData)) {
      targetElement.innerHTML = '';
      targetElement.style.display = 'none';
      targetElement.classList.add('unit-price-display--hidden');
      return true;
    }

    targetElement.style.display = '';
    targetElement.classList.remove('unit-price-display--hidden');
    return false;
  }

  /** Highest valid ERP selling price excluding location 1, independent of ranging. */
  getBrowsePrice(scope, metafields = []) {
    const prices = [];
    let hasRawPrices = false;
    const addPrice = (value) => {
      const raw = typeof value === 'number' ? String(value) : value;
      if (typeof raw !== 'string' || !/^\d+(?:\.\d+)?$/.test(raw.trim())) return;
      const price = Number(raw);
      if (Number.isFinite(price) && price > 0) prices.push(price);
    };
    const sources = [scope, ...scope.querySelectorAll('[data-location-prices], [data-browse-price], product-form')];
    sources.forEach((source) => {
      const rawLocationPrices = source.getAttribute('data-location-prices');
      if (rawLocationPrices !== null) hasRawPrices = true;
      try {
        const locationPrices = JSON.parse(rawLocationPrices || '{}');
        Object.entries(locationPrices || {}).forEach(([store, value]) => {
          if (store !== '1' && /^\d+$/.test(store)) addPrice(value);
        });
      } catch (e) {
        // Other raw attributes or metafields can still provide the price.
      }
      Array.from(source.attributes || []).forEach(({ name, value }) => {
        if (/^data-price-location-\d+$/.test(name)) {
          hasRawPrices = true;
          if (name !== 'data-price-location-1') addPrice(value);
        }
      });
    });
    metafields.forEach((field) => {
      if (field && (!field.namespace || field.namespace === 'custom') && /^price_location_\d+$/.test(field.key)) {
        hasRawPrices = true;
        if (field.key !== 'price_location_1') addPrice(field.value);
      }
    });
    // A stale precomputed maximum must not reintroduce excluded location 1.
    if (!hasRawPrices) sources.forEach((source) => addPrice(source.getAttribute('data-browse-price')));
    return prices.length ? Math.max(...prices) : null;
  }

  /** Paint browse prices from existing attributes; never fetch or populate cart properties. */
  surfaceBrowseCataloguePrices(root = document, metafields = []) {
    const priceWrappers = root.querySelectorAll(
      '.product-item .dynamic-price__wrapper, product-form .dynamic-price__wrapper'
    );

    priceWrappers.forEach((priceWrapper) => {
      const card = priceWrapper.closest('.product-item');
      const scope = card || priceWrapper.closest('product-form');
      if (!scope || this.preserveDigitalProduct(scope)) return;
      const price = this.getBrowsePrice(scope, metafields);
      const metadata = scope.hasAttribute('data-uom') ? scope : scope.querySelector('[data-uom]');
      const uom = metadata?.getAttribute('data-uom') || '';
      const conversion = Number(metadata?.getAttribute('data-conversion-factor'));
      priceWrapper.querySelectorAll('.from_price').forEach((element) => {
        element.textContent = price === null ? '' : this.formatPrice(Math.round(price * 100));
        this.setSellingUnitLabel(element, price === null ? '' : 'ea');
      });
      priceWrapper.querySelectorAll('.unit_price_display, .unit_price_per').forEach((element) => {
        element.textContent = '';
        if (price !== null && uom && Number.isFinite(conversion) && conversion > 0 &&
            !this.hideUnitPriceDisplayForEachProduce(scope, uom, element)) {
          const small = document.createElement('small');
          small.textContent = `${this.formatPrice(Math.round(price * conversion * 100))} ${this.formatCupUom(uom)}`;
          element.appendChild(small);
        }
      });
      priceWrapper.querySelectorAll('.unit_price').forEach((element) => {
        element.classList.remove('is-special', 'price-special-color');
      });
      scope.querySelectorAll('.special-badge, .additional').forEach((element) => {
        element.classList.remove('show-additional');
        element.style.removeProperty('display');
        if (element.classList.contains('additional')) element.textContent = '';
      });
      // A previous store selection must not leave purchasable location properties behind.
      scope.querySelectorAll('[data-location-price], [data-location-id]').forEach((input) => {
        input.value = '';
      });
      priceWrapper.classList.remove('price-visually-hidden');
      priceWrapper.classList.add('price-loaded');

      const formScope = priceWrapper.closest('product-form') || scope;
      const productIDInput = formScope.querySelector('input[name="id"]');
      const submitButton = formScope.querySelector('.submit-button, .product-form__submit');
      if (productIDInput) productIDInput.disabled = false;
      if (submitButton) submitButton.disabled = false;
      this.restoreAddButtonLabel(formScope);
      if (card) {
        card.classList.add('card-in-stock');
        card.classList.remove('location-unavailable');
        card.setAttribute('data-processed', 'true');
      }
    });
    return priceWrappers.length;
  }

  /**
   * Initialize the product card data manager
   */
  init() {
    if (!this.locationManager) {
      console.error('LocationManager not found. Make sure location-manager.js is loaded first.');
      return;
    }

    if (this.DEBUG) console.log('ProductCardDataManager initialized');
    this.setupEventListeners();

    // Check if we're on a collection page (has product-grid with collection handle)
    const productGrid = document.getElementById('product-grid');
    const isCollectionPage = productGrid && productGrid.hasAttribute('data-collection-handle');
    const hasServerLocationFilter = window.location.search.includes('filter.p.m.custom.available_locations');

    if (!this.hasExplicitStore()) {
      // The full ERP price map is already present in Liquid card attributes. Scan it
      // locally; selecting a store still triggers the normal store-aware reprocess.
      if (this.DEBUG) console.log('Browse mode: displaying highest location prices without GraphQL');
      this.surfaceBrowseCataloguePrices();

      // filterCollectionByLocation() is a same-page DOM/attribute read (no network, no
      // network requests) - it is kept here because
      // it's what flips #product-grid's data-location-validating attribute off (see
      // main-collection-product-grid.liquid), which otherwise leaves every .product-item
      // permanently `visibility: hidden` via CSS.
      if (isCollectionPage && !hasServerLocationFilter) {
        this.filterCollectionByLocation();
      }

      // The search-results loading overlay normally waits on processAllProductCards() to
      // hide it; that call is skipped in browse mode, so clear it immediately instead of
      // waiting on the 5s failsafe timeout further down this file.
      if (productGrid) productGrid.classList.add('product-grid--loaded');
      if (document.body.classList.contains('template-search')) {
        const resultsLoadingOverlay = document.querySelector('.results-loading-overlay');
        if (resultsLoadingOverlay) resultsLoadingOverlay.style.display = 'none';
      }

      return;
    }

    if (isCollectionPage && !hasServerLocationFilter) {
      console.log('📦 Collection page detected - filtering by available_locations');
      this.filterCollectionByLocation();
    }
    
    // Always process all product cards for pricing
    this.processAllProductCards();
    
    this.processMainProductForm(); // Handle main product form on product pages
  }

  /**
   * Filter collection products by available_locations metafield
   * Hides products not available at the current location
   */
  filterCollectionByLocation() {
    const productGrid = document.getElementById('product-grid');
    const hasStore = this.hasExplicitStore();
    // Browse mode (Option B): union catalogue - never filter to the silent 76
    // default. getCollectionLocation() is only meaningful once a store is chosen.
    const currentLocation = hasStore ? this.getCollectionLocation(productGrid) : null;
    
    if (!productGrid) {
      console.warn('Product grid not found');
      return;
    }
    
    const productItems = productGrid.querySelectorAll('.product-item');
    let visibleCount = 0;
    let hiddenCount = 0;
    const hiddenEmptyLocations = [];
    const hiddenNotAtLocation = [];
    
    console.log(`🔍 Filtering ${productItems.length} products for ${hasStore ? `location ${currentLocation}` : 'browse (union catalogue)'}...`);
    
    productItems.forEach((item, index) => {
      if (this.preserveDigitalProduct(item)) {
        visibleCount++;
        return;
      }
      const availableLocationsAttr = item.getAttribute('data-available-locations');
      const productHandle = item.getAttribute('data-product-handle') || `product-${index}`;
      
      // If no available_locations attribute, show the product (metafield not set yet)
      if (!availableLocationsAttr) {
        if (this.DEBUG) console.log(`   [${productHandle}] No available_locations - showing (metafield not set)`);
        visibleCount++;
        return;
      }
      
      try {
        const availableLocations = JSON.parse(availableLocationsAttr);
        
        // If empty array, hide the product (no inventory anywhere)
        if (!Array.isArray(availableLocations) || availableLocations.length === 0) {
          item.classList.add('location-unavailable');
          hiddenCount++;
          hiddenEmptyLocations.push(productHandle);
          return;
        }
        
        // Browse mode: any non-empty available_locations qualifies for the union
        // catalogue - don't filter down to a single (silent-default) store.
        if (!hasStore) {
          item.classList.remove('location-unavailable');
          visibleCount++;
          return;
        }
        
        // Check if current location is in the available locations
        const isAvailable = availableLocations.includes(currentLocation) || 
                           availableLocations.includes(String(currentLocation));
        
        if (isAvailable) {
          item.classList.remove('location-unavailable');
          visibleCount++;
          if (this.DEBUG) console.log(`   [${productHandle}] ✓ Available at location ${currentLocation}`);
        } else {
          item.classList.add('location-unavailable');
          hiddenCount++;
          hiddenNotAtLocation.push(productHandle);
        }
      } catch (e) {
        // JSON parse error - show the product
        console.warn(`   [${productHandle}] Failed to parse available_locations:`, e);
        visibleCount++;
      }
    });
    
    console.log(`✅ Location filter complete: ${visibleCount} visible, ${hiddenCount} hidden`);
    // Always-on removal audit: exactly which cards this script hid, and why.
    if (hiddenEmptyLocations.length > 0) {
      console.log(`🗑️ Hidden (empty available_locations - no stores at all): ${hiddenEmptyLocations.length}`, hiddenEmptyLocations);
    }
    if (hiddenNotAtLocation.length > 0) {
      console.log(`🗑️ Hidden (not ranged at store ${currentLocation}): ${hiddenNotAtLocation.length}`, hiddenNotAtLocation);
    }
    
    // Update product count display
    this.updateProductCount(visibleCount);
    
    // Mark grid as filtered
    productGrid.classList.add('filtered');
    productGrid.classList.remove('filtering');
  }

  getCollectionLocation(productGrid = document.getElementById('product-grid')) {
    const urlLocation = new URL(window.location.href).searchParams.get('filter.p.m.custom.available_locations');

    return urlLocation ||
      productGrid?.getAttribute('data-location-to-use') ||
      this.locationManager.getCurrentLocation();
  }
  
  /**
   * Update the product count display
   * Only updates if URL doesn't have location filter (client-side filtering)
   * If URL has filter, Shopify did server-side filtering and Liquid count is correct
   */
  updateProductCount(count) {
    const urlHasFilter = window.location.search.includes('filter.p.m.custom.available_locations');
    
    // Always remove loading class
    document.querySelectorAll('.product-count__text').forEach(el => {
      el.classList.remove('loading');
    });
    
    if (typeof window.updateCollectionAvailabilityState === 'function') {
      window.updateCollectionAvailabilityState(count, this.getCollectionLocation());
    } else if (count === 0) {
      document.querySelectorAll('#main-collection-filters, .facets-vertical-sort, .pagination-wrapper').forEach(el => {
        el.hidden = true;
        el.style.setProperty('display', 'none', 'important');
      });
    }

    // Update count text for client-side filtering and for empty server-filtered collection pages.
    if (!urlHasFilter || count === 0) {
      const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
      countElements.forEach(el => {
        el.textContent = `${count} products`;
      });
      console.log(`📊 Updated product count to: ${count} products (client-side filtered)`);
    } else {
      console.log(`📊 Product count preserved from Liquid (server-side filtered, page shows ${count})`);
    }
  }

  /**
   * Setup event listeners for location changes
   */
  setupEventListeners() {
    document.addEventListener('locationChanged', () => {
      console.log('Location changed, refreshing product cards...');
      
      // Check if we're on a collection page - re-filter by location
      const productGrid = document.getElementById('product-grid');
      const isCollectionPage = productGrid && productGrid.hasAttribute('data-collection-handle');
      const hasServerLocationFilter = window.location.search.includes('filter.p.m.custom.available_locations');
      
      if (isCollectionPage && !hasServerLocationFilter) {
        console.log('📦 Re-filtering collection by new location');
        this.filterCollectionByLocation();
      }
      
      // Always process all product cards on location change, force reprocessing
      this.processAllProductCards(true);
      
      this.processMainProductForm(); // Re-process main product form on location change
    });

    // Browse prices also need repainting when section rendering replaces only
    // the price, or reuses cards previously marked as processed.
    const observer = new MutationObserver((mutations) => {
      const browseScopes = new Set();
      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList') return;
        const addedElements = Array.from(mutation.addedNodes)
          .filter(node => node.nodeType === Node.ELEMENT_NODE);
        const newProductCards = addedElements.flatMap(node => {
          if (node.classList?.contains('product-item')) return [node];
          return Array.from(node.querySelectorAll?.('.product-item') || []);
        });

        if (!this.hasExplicitStore()) {
          newProductCards.forEach(card => browseScopes.add(card));
          addedElements.forEach(node => {
            if (node.matches('.dynamic-price__wrapper, .unit_price, .from_price') ||
                node.querySelector('.dynamic-price__wrapper, .unit_price, .from_price')) {
              const scope = node.closest('.product-item') || node.closest('product-form');
              if (scope) browseScopes.add(scope);
            }
          });
        } else {
          const pendingCards = newProductCards.filter(card =>
            !card.hasAttribute('data-graphql-card') && !card.hasAttribute('data-processed'));
          if (pendingCards.length) this.processProductCards(pendingCards);
        }
      });
      browseScopes.forEach(scope => this.surfaceBrowseCataloguePrices(scope));
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process all product cards on the page
   */
  processAllProductCards(forceReprocess = false) {
    if (!this.hasExplicitStore()) this.surfaceBrowseCataloguePrices();
    const productCards = document.querySelectorAll('.product-item');
    if (this.DEBUG) console.log(`Processing ${productCards.length} product cards...`);
    
    // If forcing reprocess (e.g., location change), remove data-processed attribute
    if (forceReprocess) {
      productCards.forEach(card => {
        if (!card.hasAttribute('data-graphql-card')) {
          card.removeAttribute('data-processed');
        }
      });
    }
    
    this.processProductCards(Array.from(productCards));
  }

  /**
   * Process a batch of product cards
   */
  processProductCards(productCards) {
    if (this.isProcessing) {
      console.log('Already processing cards, skipping...');
      return;
    }

    productCards.forEach(productCard => {
      this.processProductCard(productCard);
    });

    this.updateCartCollectionVisibility();
    
    // Mark product grid as loaded
    const productGrid = document.getElementById('product-grid');
    if (productGrid) {
      productGrid.classList.add('product-grid--loaded');
    }
    
    // Hide loading overlays based on page type
    const isSearchPage = document.body.classList.contains('template-search');
    const isCollectionPage = document.body.classList.contains('template-collection');
    
    if (isSearchPage) {
      // Hide search results loading overlay
      const resultsLoadingOverlay = document.querySelector('.results-loading-overlay');
      if (resultsLoadingOverlay) {
        resultsLoadingOverlay.style.display = 'none';
        console.log('✅ Hiding search loading overlay (product-card-data)');
      }
    }
    
    if (isCollectionPage) {
      // Hide collection loading overlay
      const loadingOverlay = document.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.style.display = 'none';
      }
      // Add collection class to show products are ready
      const collection = document.querySelector('.collection');
      if (collection) {
        collection.classList.remove('is-loading');
      }
      
      // Recount visible products after pricing processing (some may have been hidden)
      this.recountVisibleProducts();
    }
  }

  updateCartCollectionVisibility() {
    document.querySelectorAll('.go-cart__collection').forEach(collection => {
      const items = Array.from(collection.querySelectorAll('.upsell-item'));
      // The drawer's empty-cart block is now category tiles with no product
      // cards at all - zero upsell items must not be treated as "everything
      // was hidden by location filtering".
      if (items.length === 0) {
        collection.classList.remove('location-empty');
        collection.setAttribute('data-location-validating', 'false');
        return;
      }
      const visibleItems = items.filter(item => {
        const card = item.querySelector('.product-card, .product-item');
        return card &&
          !card.classList.contains('location-unavailable') &&
          !card.classList.contains('hidden') &&
          card.style.display !== 'none';
      });

      collection.classList.toggle('location-empty', visibleItems.length === 0);
      collection.setAttribute('data-location-validating', 'false');
    });
  }
  
  /**
   * Recount visible products after processing
   */
  recountVisibleProducts() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const allItems = productGrid.querySelectorAll('.product-item');
    let visibleCount = 0;
    
    allItems.forEach(item => {
      // Count as visible if not hidden
      if (!item.classList.contains('location-unavailable') && 
          item.style.display !== 'none' &&
          !item.classList.contains('hidden')) {
        visibleCount++;
      }
    });
    
    console.log(`📊 Recounted visible products: ${visibleCount}`);
    this.updateProductCount(visibleCount);
  }

  /**
   * Process a single product card
   */
  processProductCard(productCard) {
    if (this.preserveDigitalProduct(productCard)) return;
    const productTitle = productCard.getAttribute('data-title') || 'Unknown';
    
    // Skip GraphQL-generated cards (they're already fully rendered)
    if (productCard.hasAttribute('data-graphql-card')) {
      return;
    }
    
    // Skip Liquid-rendered cards (search page with pure Liquid pricing)
    if (productCard.hasAttribute('data-liquid-rendered')) {
      return;
    }
    
    // Skip if already processed
    if (productCard.hasAttribute('data-processed')) {
      return;
    }

    // No explicit store: scan the existing ERP price map without a GraphQL request.
    if (!this.hasExplicitStore()) {
      this.surfaceBrowseCataloguePrices(productCard);
      return;
    }

    if (this.DEBUG) console.log(`[ProcessCard] Starting: ${productTitle}`);

    if (productCard.hasAttribute('data-frozen') && productCard.getAttribute('data-frozen') === 'true') {
      if (this.DEBUG) console.log(`[${productTitle}] Removing frozen product`);
      this.removeProductCard(productCard);
      return;
    }

    // Check for JSON data attributes first (collection pages, search results, featured collections)
    // This is the primary path for Liquid-rendered pages
    try {
      const locationPricesJson = productCard.getAttribute('data-location-prices');
      if (locationPricesJson) {
        if (this.DEBUG) console.log(`[${productTitle}] Found JSON data attributes, processing...`);
        this.processProductCardWithJsonDataFromCard(productCard, locationPricesJson);
        productCard.setAttribute('data-processed', 'true');
        return;
      }
    } catch (e) {
      console.error(`[${productTitle}] Error processing JSON data attributes:`, e);
    }
    
    // Check for JSON data on nested product-form element (older format)
    try {
      const productForm = productCard.querySelector('product-form');
      const locationPricesJson = productForm?.getAttribute('data-location-prices');
      if (locationPricesJson) {
        if (this.DEBUG) console.log(`[${productTitle}] Found JSON data on product-form, processing...`);
        this.processProductCardWithJsonData(productCard, locationPricesJson);
        productCard.setAttribute('data-processed', 'true');
        return;
      }
    } catch (e) {
      console.error(`[${productTitle}] Error processing product-form JSON data:`, e);
    }

    // Check for very old variant-based system (has data-all-variants)
    const variants = productCard.getAttribute('data-all-variants');
    if (variants) {
      if (this.DEBUG) console.log(`[${productTitle}] Processing old variant-based system (data-all-variants)`);
      this.processLegacyProductCard(productCard, variants);
      productCard.setAttribute('data-processed', 'true');
      return;
    }

    // Check for legacy system with individual price attributes (predictive search).
    // Browse pricing already scanned every individual price attribute above.
    // Selected-store validation below must still use only that store's value.
    if (this.hasExplicitStore()) {
      const currentLocation = this.locationManager.getCurrentLocation();
      const priceAttr = productCard.getAttribute(`data-price-location-${currentLocation}`);

      if (priceAttr !== null) {
        if (this.DEBUG) console.log(`[${productTitle}] Has individual price attribute, but missing data-all-variants`);
        // Card has price but no variant data - this shouldn't happen, but handle gracefully
        // Remove card if price is invalid
        if (priceAttr === '' || priceAttr === 'null' || parseFloat(priceAttr) <= 0) {
          if (this.DEBUG) console.log(`[${productTitle}] Invalid price, removing card`);
          this.removeProductCard(productCard);
          return;
        }
        // If we get here, card has a valid price but no variants - mark as processed
        productCard.setAttribute('data-processed', 'true');
        return;
      }
    }

    // Check for new GraphQL system (collection pages)
    const productHandle = productCard.getAttribute('data-product-handle');
    if (productHandle) {
      if (this.DEBUG) console.log(`[${productTitle}] Found GraphQL product card with handle`);
      // New GraphQL system - add to batch queue for efficient processing
      this.addToBatchQueue(productHandle, productCard);
      return;
    }
    
    if (this.DEBUG) console.warn(`[${productTitle}] ⚠️ Product card missing all required data attributes:`, productCard);
    // Remove card if we can't process it
    this.removeProductCard(productCard);
  }

  /**
   * Helper method to remove a product card (and its carousel wrapper if present)
   */
  removeProductCard(productCard) {
    // Check if this card is inside a carousel slide wrapper
    const carouselSlide = productCard.closest('.upsell-item, .swiper-slide');
    if (carouselSlide && carouselSlide !== productCard) {
      console.log('   Removing carousel slide wrapper');
      carouselSlide.remove();
    } else {
      productCard.remove();
    }
  }

  /**
   * Process product card with JSON data attributes directly on the card element
   * Used for Liquid-rendered collection pages
   */
  processProductCardWithJsonDataFromCard(productCard, locationPricesJson) {
    if (this.preserveDigitalProduct(productCard)) return;
    const productGrid = productCard.closest('#product-grid');
    const hasStore = this.hasExplicitStore();
    // Browse mode (Option B): never key display/filter off the silent 76 default.
    const pickingStore = hasStore ? this.getCollectionLocation(productGrid) : null;
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const locationPriceInput = productCard.querySelector('[data-location-price]');
    const locationIdInput = productCard.querySelector('[data-location-id]');
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const productTitle = productCard.getAttribute('data-title') || 'Unknown Product';
    
    try {
      // Parse JSON data attributes from the card element
      const locationPrices = JSON.parse(locationPricesJson);
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      const locationUnitPricesJson = productCard.getAttribute('data-location-unit-prices');
      const locationAdditionalsJson = productCard.getAttribute('data-location-additionals');
      
      const locationSpecialTexts = locationSpecialTextsJson ? JSON.parse(locationSpecialTextsJson) : {};
      const locationUnitPrices = locationUnitPricesJson ? JSON.parse(locationUnitPricesJson) : {};
      const locationAdditionals = locationAdditionalsJson ? JSON.parse(locationAdditionalsJson) : {};
      
      // Get UOM and conversion from card data attributes
      const uom = productCard.getAttribute('data-uom') || '';
      const conversionFactor = productCard.getAttribute('data-conversion-factor') || '1';
      const overrideCupValue = productCard.getAttribute('data-overridecupvalue') || '';
      
      // Get location-specific data (store mode only - stays undefined in browse mode)
      const locationPrice = hasStore ? locationPrices[pickingStore] : null;
      const locationSpecialText = hasStore ? locationSpecialTexts[pickingStore] : null;
      const locationUnitPrice = hasStore ? locationUnitPrices[pickingStore] : null;
      const locationAdditional = hasStore ? locationAdditionals[pickingStore] : null;
      
      if (this.DEBUG) console.log(`[${productTitle}] Location: ${hasStore ? pickingStore : 'browse (no store)'}, Price: "${locationPrice}"`);
      
      // Check available_locations - cart drawer recommendations should match Search & Discovery availability.
      const productForm = productCard.querySelector('product-form');
      const availableLocations = productCard.getAttribute('data-available-locations') ||
        productForm?.getAttribute('data-available-locations');
      const requireAvailableLocations = productCard.closest('.go-cart__collection');

      if (requireAvailableLocations && (!availableLocations || availableLocations === 'null' || availableLocations === '[]')) {
        console.log(`🗑️ [${productTitle}] Hidden card - available_locations missing/empty (cart recommendation)`);
        productCard.classList.add('location-unavailable');
        return;
      }

      if (availableLocations) {
        try {
          const locationsArray = JSON.parse(availableLocations);
          if (!Array.isArray(locationsArray) || locationsArray.length === 0) {
            console.log(`🗑️ [${productTitle}] Hidden card - empty available_locations`);
            productCard.classList.add('location-unavailable');
            return;
          }

          // Browse mode (Option B): any non-empty available_locations qualifies
          // for the union catalogue - never filter down to a single store.
          if (hasStore && Array.isArray(locationsArray) && locationsArray.length > 0) {
            if (!locationsArray.includes(pickingStore) && !locationsArray.includes(String(pickingStore))) {
              console.log(`🗑️ [${productTitle}] Hidden card - not ranged at store ${pickingStore}`);
              productCard.classList.add('location-unavailable');
              return;
            }
          }
        } catch (e) {
          // Continue if parse fails
        }
      }

      if (!hasStore) {
        this.surfaceBrowseCataloguePrices(productCard);
        return;
      }

      // Remove card if no valid price for current location
      if (!locationPrice || locationPrice === '' || locationPrice === 'null' || parseFloat(locationPrice) <= 0) {
        console.log(`🗑️ [${productTitle}] Hidden card - no valid price for store ${pickingStore} (got "${locationPrice}")`);
        productCard.classList.add('location-unavailable');
        return;
      }
      
      // Update price display
      if (priceWrapper) {
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        if (fromPriceElement) {
          const priceFloat = parseFloat(locationPrice);
          const formattedPrice = this.formatPrice(Math.round(priceFloat * 100));
          fromPriceElement.innerHTML = formattedPrice;
          this.setSellingUnitLabel(fromPriceElement, 'ea');
        }
        
        // Update unit price display
        const unitPriceDisplay = priceWrapper.querySelector('.unit_price_display');
        if (unitPriceDisplay && this.hideUnitPriceDisplayForEachProduce(productCard, uom, unitPriceDisplay)) {
          // Fruit/veg sold as each already shows the each price in the main price.
        } else if (unitPriceDisplay && uom && uom.trim()) {
          const priceFloat = parseFloat(locationPrice);
          const unitPriceFloat = locationUnitPrice ? parseFloat(locationUnitPrice) : 0;
          const cleanUom = this.formatCupUom(uom);
          
          // If no unit price OR override is "Yes", calculate
          if (!unitPriceFloat || unitPriceFloat === 0 || overrideCupValue === "Yes") {
            if (conversionFactor) {
              const conversionFloat = parseFloat(conversionFactor);
              const calculatedUnitPrice = (priceFloat * conversionFloat).toFixed(2);
              unitPriceDisplay.innerHTML = `<small>$${calculatedUnitPrice} ${cleanUom}</small>`;
            } else {
              unitPriceDisplay.innerHTML = '';
            }
          } else {
            // Use the unit price from metafield
            unitPriceDisplay.innerHTML = `<small>$${unitPriceFloat.toFixed(2)} ${cleanUom}</small>`;
          }
        }
        
        // Remove price-visually-hidden class to show price
        priceWrapper.classList.remove('price-visually-hidden');
      }
      
      // Update special badge and pricing.
      // custom.special_locations is the only source of truth for status.
      const specialBadge = productCard.querySelector('.special-badge');
      const unitPriceElement = priceWrapper ? priceWrapper.querySelector('.unit_price') : null;
      const specialLocationsAttr =
        productCard.getAttribute('data-special-locations') ||
        productCard.closest('.product-item')?.getAttribute('data-special-locations');
      const isOnSpecial = this.isOnSpecialAtLocation(specialLocationsAttr, pickingStore);

      // Skip client hide when S&D already filtered by special_locations.
      const hasServerSpecialsFilter = window.location.search.includes(
        'filter.p.m.custom.special_locations'
      );
      if (
        productGrid &&
        productGrid.hasAttribute('data-specials-collection') &&
        !hasServerSpecialsFilter &&
        !isOnSpecial
      ) {
        console.log(`🗑️ [${productTitle}] Hidden card - not on special at store ${pickingStore} (specials collection)`);
        productCard.classList.add('location-unavailable');
        return;
      }
      
      if (isOnSpecial) {
        if (specialBadge) specialBadge.style.display = 'block';
        if (unitPriceElement) unitPriceElement.classList.add('is-special');
      } else {
        if (specialBadge) specialBadge.style.display = 'none';
        if (unitPriceElement) unitPriceElement.classList.remove('is-special');
      }
      
      // Update additional text — the "Save $X" additional metafield only makes
      // sense while the product is on special, so it never shows otherwise.
      if (priceWrapper) {
        const additionalElement = priceWrapper.querySelector('.additional');
        if (additionalElement) {
          if (isOnSpecial && locationSpecialText && locationSpecialText !== '-' && locationSpecialText.trim()) {
            additionalElement.innerHTML = locationSpecialText;
            additionalElement.classList.add('show-additional');
          } else if (isOnSpecial && locationAdditional && locationAdditional.trim() && locationAdditional !== '-') {
            additionalElement.innerHTML = locationAdditional;
            additionalElement.classList.add('show-additional');
          } else {
            additionalElement.innerHTML = '';
            additionalElement.classList.remove('show-additional');
          }
        }
      }
      
      // Update location price inputs for cart
      if (locationPriceInput) locationPriceInput.value = locationPrice;
      if (locationIdInput) locationIdInput.value = pickingStore;
      
      // Enable the form
      if (productIDInput) productIDInput.disabled = false;
      if (submitButton) submitButton.disabled = false;
      
      // Show the card
      productCard.classList.add('card-in-stock');
      productCard.classList.remove('location-unavailable');
      this.restoreAddButtonLabel(productCard);
      
      if (this.DEBUG) console.log(`[${productTitle}] ✓ Enabled - Price: $${locationPrice}`);
      
    } catch (e) {
      console.error('Error processing card JSON data:', e);
      productCard.classList.add('location-unavailable');
    }
  }

  /**
   * Process product card with JSON data attributes (for search/predictive search)
   */
  processProductCardWithJsonData(productCard, locationPricesJson) {
    if (this.preserveDigitalProduct(productCard)) return;
    const hasStore = this.hasExplicitStore();
    // Browse mode (Option B): never key off the silent 76 default.
    const pickingStore = hasStore ? this.locationManager.getCurrentLocation() : null;
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const locationPriceInput = productCard.querySelector('[data-location-price]');
    const locationIdInput = productCard.querySelector('[data-location-id]');
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const productTitle = productCard.getAttribute('data-title') || 'Unknown Product';
    
    try {
      // Parse JSON data attributes
      const locationPrices = JSON.parse(locationPricesJson);
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      const locationUnitPricesJson = productCard.getAttribute('data-location-unit-prices');
      const locationAdditionalsJson = productCard.getAttribute('data-location-additionals');
      const specialLocationsAttr =
        productCard.getAttribute('data-special-locations') ||
        productCard.closest('.product-item')?.getAttribute('data-special-locations');
      
      const locationSpecialTexts = locationSpecialTextsJson ? JSON.parse(locationSpecialTextsJson) : {};
      const locationUnitPrices = locationUnitPricesJson ? JSON.parse(locationUnitPricesJson) : {};
      const locationAdditionals = locationAdditionalsJson ? JSON.parse(locationAdditionalsJson) : {};
      
      // Get location-specific data (store mode only)
      const locationPrice = hasStore ? locationPrices[pickingStore] : null;
      const locationSpecialText = hasStore ? locationSpecialTexts[pickingStore] : null;
      const locationUnitPrice = hasStore ? locationUnitPrices[pickingStore] : null;
      const locationAdditional = hasStore ? locationAdditionals[pickingStore] : null;
      const isOnSpecial = hasStore ? this.isOnSpecialAtLocation(specialLocationsAttr, pickingStore) : false;
      
      if (this.DEBUG) console.log(`[${productTitle}] Location: ${hasStore ? pickingStore : 'browse (no store)'}, Price: "${locationPrice}", Type: ${typeof locationPrice}`);
      
      // Check available_locations (union catalogue in browse mode) before any
      // store-specific gating below.
      const productFormEl = productCard.querySelector('product-form');
      const availableLocationsEarly = productCard.getAttribute('data-available-locations') ||
        productFormEl?.getAttribute('data-available-locations');
      if (availableLocationsEarly) {
        try {
          const locationsArrayEarly = JSON.parse(availableLocationsEarly);
          if (Array.isArray(locationsArrayEarly) && locationsArrayEarly.length === 0) {
            if (this.DEBUG) console.log(`[${productTitle}] No inventory at any location, removing card`);
            this.removeProductCard(productCard);
            return;
          }
        } catch (e) {
          // Continue - don't hide on parse error
        }
      }

      if (!hasStore) {
        this.surfaceBrowseCataloguePrices(productCard);
        return;
      }

      // Check hideInState
      const hideInState = productCard.getAttribute('data-hideinstate') || '';
      if (hideInState && hideInState !== '-' && hideInState.includes(pickingStore.toString())) {
        if (this.DEBUG) console.log(`[${productTitle}] Hidden in location ${pickingStore}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Remove card if no valid price for current location
      if (!locationPrice || locationPrice === '' || locationPrice === 'null' || parseFloat(locationPrice) <= 0) {
        if (this.DEBUG) console.log(`[${productTitle}] No valid price for location ${pickingStore}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Check if location has inventory (available_locations metafield)
      // Only enforce if metafield exists - allows products to show until Flow populates the field
      const productForm = productCard.querySelector('product-form');
      const availableLocations = productForm ? productForm.getAttribute('data-available-locations') : null;
      
      if (availableLocations && availableLocations !== 'null' && availableLocations !== '') {
        if (this.DEBUG) console.log(`[${productTitle}] Checking available_locations: "${availableLocations}"`);
        try {
          const locationsArray = JSON.parse(availableLocations);
          
          // If array is empty, product has no inventory anywhere
          if (Array.isArray(locationsArray) && locationsArray.length === 0) {
            if (this.DEBUG) console.log(`[${productTitle}] No inventory at any location, removing card`);
            this.removeProductCard(productCard);
            return;
          }
          
          // If array exists with locations, check if current location is in it
          if (Array.isArray(locationsArray) && !locationsArray.includes(pickingStore.toString())) {
            if (this.DEBUG) console.log(`[${productTitle}] No inventory at location ${pickingStore}, removing card`);
            this.removeProductCard(productCard);
            return;
          }
        } catch (e) {
          if (this.DEBUG) console.warn(`[${productTitle}] Failed to parse available_locations "${availableLocations}":`, e);
          // Continue - don't hide on parse error
        }
      }
      
      // Update price display
      if (priceWrapper) {
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        if (fromPriceElement) {
          const priceFloat = parseFloat(locationPrice);
          const formattedPrice = this.formatPrice(Math.round(priceFloat * 100));
          fromPriceElement.innerHTML = formattedPrice;
          this.setSellingUnitLabel(fromPriceElement, 'ea');
        }
        
        // Update unit price display ($/kg, etc.) - always calculate using conversion factor
        const unitPriceDisplay = priceWrapper.querySelector('.unit_price_display');
        if (unitPriceDisplay) {
          // Get UOM, conversion factor, override cup value, and location unit price
          const productForm = productCard.querySelector('product-form');
          const uom = productForm ? productForm.getAttribute('data-uom') : productCard.getAttribute('data-uom');
          const conversionFactor = productForm ? productForm.getAttribute('data-conversion-factor') : productCard.getAttribute('data-conversion-factor');
          const overrideCupValue = productForm ? productForm.getAttribute('data-overrideCupValue') : productCard.getAttribute('data-overrideCupValue');
          const locationUnitPrice = locationUnitPrices[pickingStore];
          
          if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, unitPriceDisplay)) {
            // Fruit/veg sold as each already shows the each price in the main price.
          } else if (uom && uom.trim()) {
            // Unit price display logic (matching old code):
            // if (!compareAtPrice || overrideCupValue == "Yes") → calculate
            // else → use compareAtPrice (now unit_price_location_XX)
            const priceFloat = parseFloat(locationPrice);
            const unitPriceFloat = locationUnitPrice ? parseFloat(locationUnitPrice) : 0;
            const cleanUom = this.formatCupUom(uom);
            
            // If no unit price OR override is "Yes", calculate
            if (!unitPriceFloat || unitPriceFloat === 0 || overrideCupValue === "Yes") {
              if (conversionFactor) {
                const conversionFloat = parseFloat(conversionFactor);
                const calculatedUnitPrice = (priceFloat * conversionFloat).toFixed(2);
                unitPriceDisplay.innerHTML = `<small>$${calculatedUnitPrice} ${cleanUom}</small>`;
              } else {
                unitPriceDisplay.innerHTML = '';
              }
            } else {
              // Use the unit price from metafield
              unitPriceDisplay.innerHTML = `<small>$${unitPriceFloat.toFixed(2)} ${cleanUom}</small>`;
            }
          } else {
            unitPriceDisplay.innerHTML = '';
          }
        }
      }
      
      // Update special badge and pricing (special_locations only — not special text alone)
      const specialBadge = productCard.querySelector('.special-badge');
      const unitPriceElement = priceWrapper ? priceWrapper.querySelector('.unit_price') : null;

      if (isOnSpecial) {
        if (specialBadge) specialBadge.style.display = 'block';
        if (unitPriceElement) unitPriceElement.classList.add('is-special');
      } else {
        if (specialBadge) specialBadge.style.display = 'none';
        if (unitPriceElement) unitPriceElement.classList.remove('is-special');
      }
      
      // Update additional text — special text wins, then the "Save $X"
      // additional metafield. Both only show while the product is on special.
      if (priceWrapper) {
        const additionalElement = priceWrapper.querySelector('.additional');
        if (additionalElement) {
          if (isOnSpecial && locationSpecialText && locationSpecialText !== '-' && locationSpecialText.trim()) {
            additionalElement.innerHTML = locationSpecialText;
            additionalElement.classList.add('show-additional');
          } else if (isOnSpecial && locationAdditional && locationAdditional.trim() && locationAdditional !== '-') {
            additionalElement.innerHTML = locationAdditional;
            additionalElement.classList.add('show-additional');
          } else {
            additionalElement.innerHTML = '';
            additionalElement.classList.remove('show-additional');
          }
        }
      }
      
      // Update location price inputs for cart
      if (locationPriceInput) {
        locationPriceInput.value = locationPrice;
      }
      if (locationIdInput) {
        locationIdInput.value = pickingStore;
      }
      
      // Enable the form
      if (productIDInput) {
        productIDInput.disabled = false;
      }
      if (submitButton) {
        submitButton.disabled = false;
      }
      
      // Show the card
      productCard.classList.add('card-in-stock');
      this.restoreAddButtonLabel(productCard);
      
      if (this.DEBUG) console.log(`[${productTitle}] ✓ Enabled - Price: $${locationPrice}, Special: ${isOnSpecial}`);
      
    } catch (e) {
      console.error('Error processing JSON data for product card:', e);
      this.removeProductCard(productCard);
    }
  }

  /**
   * Process legacy product card using the old variant string system
   */
  processLegacyProductCard(productCard, variants) {
    if (this.preserveDigitalProduct(productCard)) return;
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    const submitButton = productCard.querySelector('.submit-button');
    const productIDInput = productCard.querySelector('input[name="id"]');
    const itemQuantityWrapper = productCard.querySelector('.quick-add__quantity');
    const itemQuantity = productCard.querySelector('.collection-quantity-selector');
    const additionalWrapper = productCard.querySelector('.additional');
    const specialBadge = productCard.querySelector('.special-badge');
    const productForm = productCard.querySelector('product-form');

    if (!priceWrapper) {
      console.warn('⚠️ Price wrapper not found for legacy product card');
      return;
    }

    if (!this.hasExplicitStore()) {
      this.surfaceBrowseCataloguePrices(productCard);
      return;
    }

    const partsOfStr = variants.split(';').filter(v => v.trim());
    const pickingStore = this.locationManager.getCurrentLocation();
    const pickingStoreVariant = 's:' + pickingStore;
    
    // Check individual price attribute - if it exists and is empty, remove card immediately
    const individualPriceAttr = productCard.getAttribute(`data-price-location-${pickingStore}`);
    if (individualPriceAttr !== null && (individualPriceAttr === '' || individualPriceAttr === 'null' || parseFloat(individualPriceAttr) <= 0)) {
      console.log(`No valid price in data-price-location-${pickingStore}, removing card`);
      this.removeProductCard(productCard);
      return;
    }

    // Try to find variant for current location, otherwise use first available variant
    let matchingVariantArray = null;
    let fallbackVariantArray = null;

    for (let i = 0; i < partsOfStr.length; i++) {
      const variantArray = partsOfStr[i].split(',');
      if (!variantArray[4]) continue; // Skip if no variant ID
      
      if (variantArray[0].trim() === pickingStoreVariant.trim()) {
        matchingVariantArray = variantArray;
        break;
      }
      
      // Store first valid variant as fallback
      if (!fallbackVariantArray) {
        fallbackVariantArray = variantArray;
      }
    }

    // Use matching variant or fallback to first available
    const variantArray = matchingVariantArray || fallbackVariantArray;

    if (!variantArray) {
      console.warn('⚠️ No valid variants found for legacy product card');
      this.removeProductCard(productCard);
      return;
    }

    // Check if location-specific pricing is available in JSON data attributes (fast path for search/predictive search)
    let locationPrice = null;
    let locationSpecialText = null;
    const specialLocationsAttr =
      productCard.getAttribute('data-special-locations') ||
      productCard.closest('.product-item')?.getAttribute('data-special-locations');
    const isOnSpecial = this.isOnSpecialAtLocation(specialLocationsAttr, pickingStore);
    
    try {
      const locationPricesJson = productCard.getAttribute('data-location-prices');
      const locationSpecialTextsJson = productCard.getAttribute('data-location-special-texts');
      
      if (locationPricesJson) {
        const locationPrices = JSON.parse(locationPricesJson);
        locationPrice = locationPrices[pickingStore];
      }
      
      if (locationSpecialTextsJson) {
        const locationSpecialTexts = JSON.parse(locationSpecialTextsJson);
        locationSpecialText = locationSpecialTexts[pickingStore];
      }
    } catch (e) {
      // JSON parsing failed, will use fallback
      console.warn('Failed to parse location data JSON:', e);
    }
    
    // If the variant string doesn't encode this store's price directly, the JSON
    // price attribute (data-location-prices[pickingStore]) is the only trusted
    // source for the selected store's price. fallbackVariantArray belongs to a
    // DIFFERENT store - its price must never be shown/sold as-is (no synchronous
    // GraphQL fetch happens here, so "wait for GraphQL" would mean showing the
    // wrong price in the meantime; hide the card instead).
    if (!matchingVariantArray && fallbackVariantArray) {
      const priceFloat = parseFloat(locationPrice);
      const hasValidLocationPrice = locationPrice && locationPrice !== '' && locationPrice !== 'null' &&
        !isNaN(priceFloat) && priceFloat > 0;

      if (hasValidLocationPrice) {
        console.log(`Using data attributes for location ${pickingStore}`);
        variantArray[5] = Math.round(priceFloat * 100); // Convert to cents

        // Override special text if available (status comes from special_locations)
        if (locationSpecialText && locationSpecialText !== '' && locationSpecialText !== 'null') {
          variantArray[3] = locationSpecialText;
        }
      } else {
        console.log(`No valid price for store ${pickingStore} in legacy card data (fallback variant belongs to a different store); hiding card`);
        this.removeProductCard(productCard);
        return;
      }
    }

    const additional = locationSpecialText || variantArray[3];
    const price = parseInt(variantArray[5]);
    const unitPrice = parseInt(variantArray[6]);
    const conversionFactor = productCard.getAttribute('data-conversion-factor');
    const uom = productCard.getAttribute('data-uom');
    const hideInState = productForm?.dataset.hideinstate || '';
    const overrideCupValue = productCard.getAttribute('data-overridecupvalue');
    const variantId = variantArray[4];
    const quantityAvailable = parseInt(variantArray[7]);
    
    // Check if product should be hidden in current location
    const currentState = pickingStore.toString();
    if (hideInState && hideInState !== '-' && hideInState.includes(currentState)) {
      console.log(`Product hidden in location ${currentState}, removing card`);
      this.removeProductCard(productCard);
      return;
    }
    
    // Remove card if no valid price for current location
    if (!price || price <= 0 || isNaN(price)) {
      console.log(`No valid price for location ${currentState}, removing card`);
      this.removeProductCard(productCard);
      return;
    }
    
    // Remove card if no quantity available
    if (quantityAvailable <= 0 || isNaN(quantityAvailable)) {
      console.log(`Product sold out, removing card`);
      this.removeProductCard(productCard);
      return;
    }

    // Update variant ID
    if (productIDInput) {
      productIDInput.value = variantId;
      productIDInput.disabled = false; // Enable the input
    }
    productCard.setAttribute('data-variant-id', variantId);

        // Update quantity selector
        if (itemQuantityWrapper && itemQuantity) {
          itemQuantityWrapper.setAttribute('data-id', variantId);
          itemQuantity.value = 0;
          
          if (productCard.hasAttribute('data-max-quantity')) {
            const maxQuantity = productCard.getAttribute('data-max-quantity');
            itemQuantity.setAttribute('max', maxQuantity);
          } else {
            itemQuantity.setAttribute('max', quantityAvailable.toString());
          }
        }

        // Update price display
        const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
        const unitPricePerElement = priceWrapper.querySelector('.sale .unit_price_per');
        const unitPriceElement = priceWrapper.querySelector('.unit_price');

        if (fromPriceElement) {
          const formattedPrice = this.formatPrice(price);
          // Main price should never have UOM - just the dollar amount
          fromPriceElement.innerHTML = formattedPrice;
          this.setSellingUnitLabel(fromPriceElement, 'ea');
        }

        // Handle special pricing classes (special_locations only)
        if (isOnSpecial) {
          if (unitPriceElement) {
            unitPriceElement.classList.add('is-special');
            
            const productType = productCard.getAttribute('data-product-type') || '';
            if (productType.toLowerCase().includes('fruit') || productType.toLowerCase().includes('veg')) {
              unitPriceElement.classList.add('price-special-color');
              console.log(`   Added price-special-color class for ${productType}`);
            }
          }
          
          if (additionalWrapper && additional && additional !== '-' && additional !== 'undefined') {
            const savingsMatch = additional.toString().match(/[\d.]+/);
            if (savingsMatch) {
              const savingsAmount = parseFloat(savingsMatch[0]);
              if (savingsAmount > 0) {
                additionalWrapper.innerHTML = `Save $${savingsAmount.toFixed(2)}`;
                additionalWrapper.classList.add('show-additional');
                console.log(`   Set special savings: Save $${savingsAmount.toFixed(2)}`);
              } else {
                additionalWrapper.innerHTML = additional;
                additionalWrapper.classList.add('show-additional');
              }
            } else {
              additionalWrapper.innerHTML = additional;
              additionalWrapper.classList.add('show-additional');
            }
          }
        } else {
          if (unitPriceElement) {
            unitPriceElement.classList.remove('is-special', 'price-special-color');
          }
          
          if (additionalWrapper) {
            additionalWrapper.innerHTML = '';
            additionalWrapper.classList.remove('show-additional');
          }
        }

        // Update unit price (price by UOM) - supports both old (.unit_price_per) and new (.unit_price_display)
        const unitPriceDisplayElement = priceWrapper.querySelector('.unit_price_display');
        const targetElement = unitPriceDisplayElement || unitPricePerElement;
        
        if (targetElement) {
          console.log(`   Unit price calculation: unitPrice=${unitPrice}, overrideCup=${overrideCupValue}, uom=${uom}, conversion=${conversionFactor}`);
          if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement)) {
            console.log('   Hidden redundant each unit price for produce card');
            return;
          }
          
          // Fallback to "per kg" if UOM is empty (matching old code logic)
          const rawUom = (uom && uom !== '' && uom !== 'null') ? uom : 'per kg';
          const cleanUom = this.formatCupUom(rawUom);
          
          if (!unitPrice || overrideCupValue === 'Yes') {
            const priceByUOM = (price * parseFloat(conversionFactor || 1)) / 100;
            targetElement.innerHTML = `<small>$${priceByUOM.toFixed(2)} ${cleanUom}</small>`;
            console.log(`   Set calculated unit price: $${priceByUOM.toFixed(2)} ${cleanUom}`);
          } else {
            const unitPriceFormatted = this.formatPrice(unitPrice);
            targetElement.innerHTML = `<small>${unitPriceFormatted} ${cleanUom}</small>`;
            console.log(`   Set metafield unit price: ${unitPriceFormatted} ${cleanUom}`);
          }
        } else {
          console.warn('   No target element found for unit price display');
        }

        // Remove price-visually-hidden class to make price visible
        if (priceWrapper && priceWrapper.classList.contains('price-visually-hidden')) {
          priceWrapper.classList.remove('price-visually-hidden');
          console.log('   Removed price-visually-hidden class');
        }

        // Update special badge (always just says "Special")
        if (isOnSpecial && specialBadge) {
          specialBadge.innerHTML = 'Special';
          specialBadge.classList.add('show-additional');
        } else if (specialBadge) {
          specialBadge.classList.remove('show-additional');
        }

        // Set location-specific properties for cart
        const locationPriceInput = productCard.querySelector('[data-location-price]');
        const locationIdInput = productCard.querySelector('[data-location-id]');
        
        if (locationPriceInput) {
          // Convert cents to dollars
          const locationPriceInDollars = (price / 100).toFixed(2);
          locationPriceInput.value = locationPriceInDollars;
          console.log(`   Set location price: $${locationPriceInDollars}`);
        }
        
        if (locationIdInput) {
          locationIdInput.value = pickingStore;
        }

        // Show the card and enable submit button (price was validated above)
        productCard.classList.add('card-in-stock');
        if (submitButton) {
          submitButton.disabled = false;
        }
        this.restoreAddButtonLabel(productCard);
  }

  /**
   * Add product to batch queue for efficient processing
   */
  addToBatchQueue(productHandle, productCard) {
    this.batchQueue.push({ handle: productHandle, card: productCard });
    
    // Clear existing timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
    
    // Set new timeout to process batch
    this.batchTimeout = setTimeout(() => {
      this.processBatchQueue();
    }, this.batchDelay);
  }

  /**
   * Process the batch queue
   */
  async processBatchQueue() {
    if (this.batchQueue.length === 0 || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    
    try {
      // Get unique product handles
      const uniqueHandles = [...new Set(this.batchQueue.map(item => item.handle))];
      if (this.DEBUG) console.log(`Processing batch of ${uniqueHandles.length} unique products...`);

      // Fetch product data
      const productsData = await this.locationManager.getBatchProducts(uniqueHandles);
      
      if (!productsData || productsData.length === 0) {
        console.warn('⚠️ No product data received for batch');
        return;
      }

      // Update product cards with fetched data
      this.updateProductCards(this.batchQueue, productsData);
      
    } catch (error) {
      console.error('Error processing batch queue:', error);
    } finally {
      this.isProcessing = false;
      this.batchQueue = []; // Clear the queue
    }
  }

  /**
   * Update product cards with fetched data
   */
  updateProductCards(batch, productsData) {
    batch.forEach(({ handle, card }) => {
      const productData = productsData.find(p => p.handle === handle);
      if (productData) {
        this.updateProductCard(card, productData);
      } else {
        console.warn(`⚠️ No data found for product: ${handle}`);
      }
    });
  }

  /**
   * Update a single product card with product data
   */
  updateProductCard(productCard, productData) {
    if (this.preserveDigitalProduct(productCard)) return;
    try {
      const hasStore = this.hasExplicitStore();
      const currentLocation = this.locationManager.currentLocation;

      // A request started with a selected store may resolve after it was cleared.
      // Repaint from the full card price map, supplemented by returned ERP fields.
      if (!hasStore) {
        const availableLocationsMetafield = (productData.metafields || []).find(
          (m) => m && m.key === 'available_locations'
        );
        if (availableLocationsMetafield && availableLocationsMetafield.value) {
          try {
            const locationsArray = JSON.parse(availableLocationsMetafield.value);
            if (Array.isArray(locationsArray) && locationsArray.length === 0) {
              console.log(`No inventory at any location, removing card: ${productData.title}`);
              this.removeProductCard(productCard);
              return;
            }
          } catch (e) {
            // Continue - don't hide on parse error
          }
        }

        this.surfaceBrowseCataloguePrices(productCard, productData.metafields || []);

        productCard.setAttribute('data-processed', 'true');
        this.restoreAddButtonLabel(productCard);
        console.log(`Successfully updated product card (browse mode): ${productData.title}`);
        return;
      }

      console.log(`Updating product card for: ${productData.title}`);
      
      // Get location-specific metafields
      const locationPriceKey = `price_location_${currentLocation}`;
      const locationSpecialTextKey = `special_text_location_${currentLocation}`;
      const locationAdditionalKey = `additional_location_${currentLocation}`;
      const validMetafields = productData.metafields?.filter(m => m && m.key) || [];
      
      const priceMetafield = validMetafields.find(m => m.key === locationPriceKey);
      const specialLocationsMetafield = validMetafields.find(m => m.key === 'special_locations');
      const specialTextMetafield = validMetafields.find(m => m.key === locationSpecialTextKey);
      const additionalMetafield = validMetafields.find(m => m.key === locationAdditionalKey);
      const uomMetafield = validMetafields.find(m => m.key === 'uom');
      const conversionMetafield = validMetafields.find(m => m.key === 'conversion');
      const overrideCupMetafield = validMetafields.find(m => m.key === 'overridecup');
      const hideInStateMetafield = validMetafields.find(m => m.key === 'hideInState');
      const isOnSpecial = this.isOnSpecialAtLocation(specialLocationsMetafield, currentLocation);

      console.log(`   Found ${productData.metafields?.length || 0} metafields`);
      if (priceMetafield) console.log(`   Price: ${priceMetafield.value}`);
      if (uomMetafield) console.log(`   UOM: ${uomMetafield.value}`);

      // Check if product should be hidden in current location
      const hideInState = hideInStateMetafield?.value || '';
      if (hideInState && hideInState !== '-' && hideInState.includes(currentLocation.toString())) {
        console.log(`Product hidden in location ${currentLocation}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Find the appropriate variant for this location
      const locationVariant = this.findLocationVariant(productData, currentLocation);
      
      if (!locationVariant) {
        console.warn(`⚠️ No variant found for location ${currentLocation} in product: ${productData.title}`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Check if product has valid price for current location (using priceMetafield already declared above)
      const locationPrice = priceMetafield?.value;
      
      // Remove card if no valid price or not available
      if (!locationPrice || locationPrice === '' || locationPrice === '0' || parseFloat(locationPrice) <= 0) {
        console.log(`No valid price for location ${currentLocation}, removing card`);
        this.removeProductCard(productCard);
        return;
      }
      
      // Remove card if not available for sale
      if (!locationVariant.availableForSale || locationVariant.quantityAvailable <= 0) {
        console.log(`Product not available or sold out, removing card`);
        this.removeProductCard(productCard);
        return;
      }

      // Update form elements (may hide the card if the selected-store price fails
      // a final defensive check; bail out before touching price/unit displays if so)
      const formOk = this.updateFormElements(productCard, locationVariant, productData, priceMetafield, currentLocation);
      if (!formOk) return;

      // Update price display
      this.updatePriceDisplay(productCard, priceMetafield, locationVariant, uomMetafield, isOnSpecial, productData);

      // Update unit price
      this.updateUnitPrice(productCard, locationVariant, priceMetafield, uomMetafield, conversionMetafield, overrideCupMetafield, productData);

      // Update special badge / offer text (status from special_locations only)
      this.updateSpecialBadge(productCard, isOnSpecial);
      this.updateAdditionalInfo(productCard, additionalMetafield, isOnSpecial, specialTextMetafield);

      // Make price wrapper visible (remove hidden class)
      const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
      if (priceWrapper && priceWrapper.classList.contains('price-visually-hidden')) {
        priceWrapper.classList.remove('price-visually-hidden');
        console.log('   Removed price-visually-hidden class');
      }

      // Mark as processed
      productCard.setAttribute('data-processed', 'true');
      
      console.log(`Successfully updated product card: ${productData.title}`);
      
    } catch (error) {
      console.error('Error updating product card:', error, productData);
    }
  }

  /**
   * Find the appropriate variant for the current location
   */
  findLocationVariant(productData, currentLocation) {
    if (!productData.variants || !productData.variants.edges) {
      return null;
    }

    // First, try to find a variant with the location in its title
    const locationVariant = productData.variants.edges.find(({ node: variant }) => {
      return variant.title && variant.title.toLowerCase().includes(`s:${currentLocation}`);
    });

    if (locationVariant) {
      return locationVariant.node;
    }

    // Fallback to first available variant
    const firstAvailable = productData.variants.edges.find(({ node: variant }) => {
      return variant.availableForSale && variant.quantityAvailable > 0;
    });

    return firstAvailable ? firstAvailable.node : productData.variants.edges[0]?.node;
  }

  /**
   * Update form elements with variant data
   * @returns {boolean} false if the card was hidden due to a missing/invalid selected-store price
   */
  updateFormElements(productCard, variant, productData, priceMetafield, currentLocation) {
    const productIDInput = productCard.querySelector('input[name="id"]');
    const itemQuantity = productCard.querySelector('.collection-quantity-selector');
    const submitButton = productCard.querySelector('.submit-button');
    const locationPriceInput = productCard.querySelector('[data-location-price]');

    // Update variant ID
    const variantId = variant.id.split('/').pop();
    if (productIDInput) {
      productIDInput.value = variantId;
    }
    productCard.setAttribute('data-variant-id', variantId);

    // Update quantity selector
    if (itemQuantity) {
      itemQuantity.value = 0;
      
      const maxQuantity = productCard.getAttribute('data-max-quantity') || 
                         variant.quantityAvailable || 
                         20;
      itemQuantity.setAttribute('max', maxQuantity.toString());
    }
    
    // Store location-specific price for cart. Selected-store metafield only -
    // ordinary products must never fall back to Shopify's base variant price
    // or another store's price. Defensive backstop even though the caller
    // already validated this price before reaching this method.
    if (locationPriceInput) {
      const locationPrice = priceMetafield?.value;
      if (!locationPrice || parseFloat(locationPrice) <= 0) {
        console.error(`[ProductCardData] updateFormElements: missing/invalid price_location_${currentLocation} for "${productData?.title || 'unknown'}" - hiding card instead of using base/other-store price.`);
        this.removeProductCard(productCard);
        return false;
      }
      locationPriceInput.value = locationPrice;
      productCard.setAttribute('data-location-price', locationPrice);
    }

    // Show the card and enable submit button (availability was validated before calling this method)
    productCard.classList.add('card-in-stock');
    if (submitButton) {
      submitButton.disabled = false;
    }
    this.restoreAddButtonLabel(productCard);
    return true;
  }

  /**
   * Update price display
   */
  updatePriceDisplay(productCard, priceMetafield, variant, uomMetafield, isOnSpecial, productData) {
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    if (!priceWrapper) {
      console.warn(' Price wrapper not found');
      return;
    }

    const fromPriceElement = priceWrapper.querySelector('.sale .from_price');
    const unitPriceElement = priceWrapper.querySelector('.unit_price');
    if (!fromPriceElement) {
      console.warn(' From price element not found');
      return;
    }

    // Selected-store metafield price only. Ordinary products must never display
    // Shopify's base variant price - missing/invalid price hides the card.
    const locationPriceValue = priceMetafield?.value;
    if (!locationPriceValue || parseFloat(locationPriceValue) <= 0) {
      console.error(`[ProductCardData] updatePriceDisplay: missing/invalid selected-store price for "${productData?.title || 'unknown'}" - hiding card.`);
      this.removeProductCard(productCard);
      return;
    }
    const priceInCents = parseFloat(locationPriceValue) * 100;
    console.log(`   Using metafield price: $${(priceInCents/100).toFixed(2)}`);

    if (priceInCents > 0) {
      const formattedPrice = this.formatPrice(priceInCents);
      
      // Main price should never have UOM - just the dollar amount
      fromPriceElement.innerHTML = formattedPrice;
      this.setSellingUnitLabel(fromPriceElement, 'ea');
      
      // Handle special pricing classes (special_locations only)
      if (isOnSpecial) {
        if (unitPriceElement) {
          unitPriceElement.classList.add('is-special');
          
          const productType = productData.productType || '';
          if (productType.toLowerCase().includes('fruit') || productType.toLowerCase().includes('veg')) {
            unitPriceElement.classList.add('price-special-color');
            console.log(`   Added price-special-color class for ${productType}`);
          }
        }
      } else if (unitPriceElement) {
        unitPriceElement.classList.remove('is-special', 'price-special-color');
      }
      
      console.log(`   Set price to: ${fromPriceElement.innerHTML}`);
    } else {
      console.warn(' No valid price found');
    }
  }

  /**
   * Update unit price display
   */
  updateUnitPrice(productCard, variant, priceMetafield, uomMetafield, conversionMetafield, overrideCupMetafield, productData = null) {
    const priceWrapper = productCard.querySelector('.dynamic-price__wrapper');
    if (!priceWrapper) return;

    // Support both old (.unit_price_per) and new (.unit_price_display)
    const unitPriceDisplayElement = priceWrapper.querySelector('.unit_price_display');
    const unitPricePerElement = priceWrapper.querySelector('.sale .unit_price_per');
    const targetElement = unitPriceDisplayElement || unitPricePerElement;
    
    if (!targetElement) return;

    const uom = uomMetafield?.value || '';
    const conversion = parseFloat(conversionMetafield?.value || '1');
    const overrideCup = overrideCupMetafield?.value || '';

    if (this.hideUnitPriceDisplayForEachProduce(productCard, uom, targetElement, productData)) {
      return;
    }

    // Fallback to "per kg" if UOM is empty (matching old code logic)
    const rawUom = uom && uom !== '' ? uom : 'per kg';
    const cleanUom = this.formatCupUom(rawUom);

    // Selected-store metafield price only - never Shopify's base variant price.
    const locationPriceValue = priceMetafield?.value;
    let priceInCents = 0;
    if (locationPriceValue && parseFloat(locationPriceValue) > 0) {
      priceInCents = parseFloat(locationPriceValue) * 100;
    }

    if (priceInCents > 0) {
      if (overrideCup === 'Yes' || !variant.compareAtPrice) {
        // Calculate unit price using conversion factor
        const unitPrice = (priceInCents * conversion) / 100;
        targetElement.innerHTML = `<small>$${unitPrice.toFixed(2)} ${cleanUom}</small>`;
        console.log(`   Set unit price to: $${unitPrice.toFixed(2)} ${cleanUom}`);
      } else if (variant.compareAtPrice) {
        // Use compare at price as unit price
        const unitPrice = parseFloat(variant.compareAtPrice.amount);
        targetElement.innerHTML = `<small>$${unitPrice.toFixed(2)} ${cleanUom}</small>`;
        console.log(`   Set unit price to: $${unitPrice.toFixed(2)} ${cleanUom} (from compareAt)`);
      }
    }
  }

  /**
   * Update special badge (always just says "Special")
   */
  updateSpecialBadge(productCard, isOnSpecial) {
    const specialBadge = productCard.querySelector('.special-badge');
    if (!specialBadge) return;

    if (isOnSpecial) {
      specialBadge.innerHTML = 'Special';
      specialBadge.classList.add('show-additional');
      console.log(`   Set special badge to "Special"`);
    } else {
      specialBadge.classList.remove('show-additional');
    }
  }

  /**
   * Update additional info - shows special text from special_text_location_XX metafield
   */
  updateAdditionalInfo(productCard, additionalMetafield, isOnSpecial, specialTextMetafield) {
    const additionalWrapper = productCard.querySelector('.additional');
    if (!additionalWrapper) return;

    const specialText = specialTextMetafield?.value;

    // If product is on special, handle special savings display
    if (isOnSpecial) {
      // Try to parse the special text as a savings amount (e.g., "Save 2.50" or "2.50")
      if (specialText) {
        const savingsMatch = specialText.toString().match(/[\d.]+/);
        if (savingsMatch) {
          const savingsAmount = parseFloat(savingsMatch[0]);
          if (savingsAmount > 0) {
            additionalWrapper.innerHTML = `Save $${savingsAmount.toFixed(2)}`;
            additionalWrapper.classList.add('show-additional');
            console.log(`   Set special savings: Save $${savingsAmount.toFixed(2)}`);
            return;
          }
        }
        // If not a savings amount, show the special text as-is
        additionalWrapper.innerHTML = specialText;
        additionalWrapper.classList.add('show-additional');
        console.log(`   Set special text: ${specialText}`);
        return;
      }
      
      // If on special but no special text, try to use additional field
      if (additionalMetafield && additionalMetafield.value) {
        additionalWrapper.innerHTML = additionalMetafield.value;
        additionalWrapper.classList.add('show-additional');
        console.log(`   Set additional info for special: ${additionalMetafield.value}`);
        return;
      }
    }

    // Not on special: the additional "Save $X" text would be misleading, hide it.
    additionalWrapper.innerHTML = '';
    additionalWrapper.classList.remove('show-additional');
  }

  /**
   * Format price from cents to currency string
   */
  formatPrice(priceInCents) {
    return `$${(priceInCents / 100).toFixed(2)}`;
  }

  /**
   * Process main product form on product pages
   */
  async processMainProductForm() {
    console.log('Looking for main product form...');
    const productForm = document.querySelector('product-form form[data-product-id]');
    
    if (!productForm) {
      console.log('No main product form found (not on a product page)');
      return; // Not on a product page or form not found
    }

    if (this.preserveDigitalProduct(productForm)) return;

    const productCard = productForm.closest('.product-item');
    if (productCard?.hasAttribute('data-location-prices')) {
      if (this.DEBUG) console.log('Main product form already processed from Liquid location payload');
      return;
    }

    // The PDP carries the full ERP price map too; no product fetch is needed in browse.
    if (!this.hasExplicitStore()) {
      this.surfaceBrowseCataloguePrices(productCard || productForm.closest('product-form'));
      return;
    }

    const productHandle = this.getProductHandleFromURL();
    if (!productHandle) {
      console.warn('Could not determine product handle from URL');
      return;
    }

    if (this.DEBUG) console.log(`Processing main product form for: ${productHandle}`);

    try {
      const productData = await this.locationManager.getProduct(productHandle);
      
      if (!productData) {
        console.error(`Failed to fetch product data for ${productHandle}`);
        return;
      }

      if (!this.hasExplicitStore()) {
        this.surfaceBrowseCataloguePrices(productCard || productForm.closest('product-form'), productData.metafields || []);
        return;
      }

      const currentLocation = this.locationManager.currentLocation;
      const priceKey = `price_location_${currentLocation}`;
      const hideInStateKey = 'hideInState';

      // Handle nested productByHandle structure from locationManager.getProduct()
      const product = productData.productByHandle || productData;

      const validMetafields = product.metafields?.filter(m => m && m.key) || [];
      
      // DEBUG: Log all price metafields
      console.log(`📊 Product page metafields for ${productHandle}:`);
      const priceMetafields = validMetafields.filter(m => m.key.startsWith('price_location_'));
      priceMetafields.forEach(m => {
        console.log(`   - ${m.key}: ${m.value}`);
      });
      
      const priceMetafield = validMetafields.find(m => m.key === priceKey);
      const hideInStateMetafield = validMetafields.find(m => m.key === hideInStateKey);

      // Check if product is hidden at this location
      if (hideInStateMetafield?.value) {
        const hiddenStates = hideInStateMetafield.value.split(',').map(s => s.trim());
        if (hiddenStates.includes(currentLocation)) {
          console.log(`Product hidden at location ${currentLocation}`);
          this.disableProductForm(productForm, 'Product not available at your location');
          return;
        }
      }

      // Check if location has inventory (available_locations metafield)
      const availableLocationsMetafield = validMetafields.find(m => m.key === 'available_locations');
      
      if (this.DEBUG) {
        console.log(`[Product Page] ${productHandle} - available_locations metafield:`, availableLocationsMetafield);
        console.log(`[Product Page] ${productHandle} - available_locations value:`, availableLocationsMetafield?.value);
      }
      
      // STRICT CHECK: If metafield is missing, null, empty string, or empty array - HIDE product
      if (!availableLocationsMetafield || !availableLocationsMetafield.value || availableLocationsMetafield.value === '' || availableLocationsMetafield.value === '[]') {
        if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - available_locations is null/empty`);
        this.disableProductForm(productForm, 'Sold out');
        return;
      }
      
      // Parse and check the array
      try {
        let locationsArray = availableLocationsMetafield.value;
        
        // Parse if string
        if (typeof locationsArray === 'string') {
          locationsArray = JSON.parse(locationsArray);
        }
        
        // Check if array is empty
        if (!Array.isArray(locationsArray) || locationsArray.length === 0) {
          if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - Empty array`);
          this.disableProductForm(productForm, 'Sold out');
          return;
        }
        
        // Check if current location is in array
        const isAvailableAtLocation = locationsArray.includes(currentLocation) || 
                                     locationsArray.includes(String(currentLocation));
        
        if (!isAvailableAtLocation) {
          if (this.DEBUG) console.log(`[Product Page] ${productHandle}: HIDING - Location ${currentLocation} not in array [${locationsArray.join(', ')}]`);
          this.disableProductForm(productForm, 'Sold out at your location');
          return;
        }
        
        if (this.DEBUG) console.log(`[Product Page] ${productHandle}: ✓ SHOWING - Location ${currentLocation} found in array`);
      } catch (e) {
        if (this.DEBUG) console.warn(`[Product Page] ${productHandle}: HIDING - Failed to parse available_locations:`, e);
        this.disableProductForm(productForm, 'Sold out');
        return;
      }
      
      // Check if product has price at this location (only use metafields).
      // Selected-store price is mandatory - store 1/76 (or any other store) must
      // never be used as a fallback. Missing/invalid price hides the product
      // form entirely rather than showing/selling at another store's price.
      let locationPrice;

      if (priceMetafield?.value && parseFloat(priceMetafield.value) > 0) {
        // Metafield is already in dollars (not cents)
        locationPrice = parseFloat(priceMetafield.value).toFixed(2);
        console.log(`✓ Product page price for location ${currentLocation}:`);
        console.log(`   - Metafield value: $${locationPrice}`);
      } else {
        console.warn(`No valid price found at location ${currentLocation} - hiding product (no fallback to another store's price)`);
        this.disableProductForm(productForm, 'Unavailable at your selected store');
        // Hide the product form entirely
        productForm.style.display = 'none';
        return;
      }

      // Update hidden inputs
      const locationPriceInput = productForm.querySelector('[data-location-price]');
      const locationIdInput = productForm.querySelector('[data-location-id]');
      const variantIdInput = productForm.querySelector('input[name="id"]');

      if (locationPriceInput) {
        locationPriceInput.value = locationPrice;
        console.log(`Set location price: $${locationPrice}`);
      }

      if (locationIdInput) {
        locationIdInput.value = currentLocation;
      }

      if (variantIdInput) {
        variantIdInput.disabled = false;
      }

      // Update visible price display on the product page
      const fromPriceElement = document.querySelector('.from_price');
      const priceElement = document.querySelector('.price-item--regular, .price__regular .price-item');
      
      if (fromPriceElement) {
        fromPriceElement.textContent = `$${locationPrice}`;
        this.setSellingUnitLabel(fromPriceElement, 'ea');
        console.log(`Updated .from_price to $${locationPrice}`);
      }
      
      if (priceElement) {
        priceElement.textContent = `$${locationPrice}`;
        console.log(`Updated .price-item to $${locationPrice}`);
      }

      // Update unit price display on product page
      const unitPriceDisplayElement = document.querySelector('.unit_price_display');
      if (unitPriceDisplayElement) {
        const unitPriceMetafield = validMetafields.find(m => m.key === `unit_price_location_${currentLocation}`);
        const conversionMetafield = validMetafields.find(m => m.key === 'conversion');
        const overrideCupMetafield = validMetafields.find(m => m.key === 'overridecup');
        const uomMetafield = validMetafields.find(m => m.key === 'uom');
        
        // Unit price metafield is already in dollars (not cents)
        const unitPriceValue = unitPriceMetafield?.value ? parseFloat(unitPriceMetafield.value) : null;
        const conversionFactor = conversionMetafield?.value;
        const overrideCupValue = overrideCupMetafield?.value;
        const rawUom = uomMetafield?.value || '';
        const price = parseFloat(locationPrice);

        if (this.hideUnitPriceDisplayForEachProduce(productForm.closest('.product-item'), rawUom, unitPriceDisplayElement, product)) {
          console.log('Product page - Hidden redundant each unit price for produce product');
        } else {
        
          // Fallback to "per kg" if UOM is empty (matching old code logic)
          const displayUom = rawUom && rawUom !== "" ? this.formatCupUom(rawUom) : "per kg";
          
          if (!unitPriceValue || overrideCupValue === "Yes") {
            // Calculate unit price from price * conversion factor
            if (conversionFactor) {
              const calculatedUnitPrice = (price * parseFloat(conversionFactor)).toFixed(2);
              unitPriceDisplayElement.innerHTML = `<small>$${calculatedUnitPrice} ${displayUom}</small>`;
              console.log(`Product page - Set calculated unit price: $${calculatedUnitPrice} ${displayUom}`);
            }
          } else {
            // Use unit_price metafield (already converted from cents to dollars)
            unitPriceDisplayElement.innerHTML = `<small>$${unitPriceValue.toFixed(2)} ${displayUom}</small>`;
            console.log(`Product page - Set metafield unit price: $${unitPriceValue.toFixed(2)} ${displayUom}`);
          }
        }
      }

      // Add price-loaded class to make price visible
      const priceWrapper = document.querySelector('.dynamic-price__wrapper');
      if (priceWrapper) {
        priceWrapper.classList.remove('price-visually-hidden');
        priceWrapper.classList.add('price-loaded');
        console.log('Added price-loaded class to show correct location price');
      }

      // Reaching here means the product passed the available_locations + price checks above,
      // i.e. it IS available at the picking store. Availability is location-based, not driven by
      // Shopify's global inventory, so enable the form (enableProductForm also restores the add
      // label in case the server rendered the global "Sold out"). Genuinely unavailable cases
      // ("Sold out" / "Sold out at your location") already returned early above.
      this.enableProductForm(productForm);

    } catch (error) {
      console.error('Error processing main product form:', error);
    }
  }

  /**
   * Get product handle from URL
   */
  getProductHandleFromURL() {
    const pathParts = window.location.pathname.split('/');
    const productsIndex = pathParts.indexOf('products');
    
    if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
      return pathParts[productsIndex + 1];
    }

    return null;
  }

  /**
   * Enable product form
   */
  enableProductForm(form) {
    const submitButton = form.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-disabled');
      // Availability is location-based; if the server rendered the global "Sold out" label,
      // restore the add state now that availability at the picking store is confirmed.
      this.restoreAddButtonLabel(form);
      console.log('Product form enabled');
    }
  }

  /**
   * Disable product form with message
   */
  disableProductForm(form, message = 'Unavailable') {
    const submitButton = form.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      
      const buttonText = submitButton.querySelector('.js-go-cart-add-to-cart, span');
      if (buttonText) {
        const originalText = buttonText.textContent;
        if (!originalText.includes(message)) {
          buttonText.textContent = message;
        }
      }
      
      console.log(`Product form disabled: ${message}`);
    }
  }

  /**
   * Get current state for debugging
   */
  getCurrentState() {
    return {
      batchQueueLength: this.batchQueue.length,
      isProcessing: this.isProcessing,
      currentLocation: this.locationManager?.currentLocation
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productCardDataManager = new ProductCardDataManager();
  window.productCardDataManager.init();
  
  // Failsafe: If processing takes too long on SEARCH pages, force-remove loading overlay
  // (Collection pages are managed by graphql-collection-manager.js)
  setTimeout(() => {
    const isSearchPage = document.body.classList.contains('template-search');
    
    if (isSearchPage) {
      const productGrid = document.getElementById('product-grid');
      if (productGrid && !productGrid.classList.contains('product-grid--loaded')) {
        console.warn('⏱️ Search loading overlay timeout - forcing removal');
        productGrid.classList.add('product-grid--loaded');
        
        const resultsLoadingOverlay = document.querySelector('.results-loading-overlay');
        if (resultsLoadingOverlay) {
          resultsLoadingOverlay.style.display = 'none';
        }
      }
    }
  }, 5000); // 5 second timeout
});

// Legacy function compatibility
window.createProductCard = function() {
  if (window.productCardDataManager) {
    window.productCardDataManager.processAllProductCards();
  }
};

window.processProductCard = function(productCard) {
  if (window.productCardDataManager) {
    window.productCardDataManager.processProductCard(productCard);
  }
}; 
